/**
 * Questionnaire Sync Service
 * 
 * Transforms questionnaire answers into the lifePlan state structure
 */

import { allQuestions } from '@/data/questionnaireQuestions'
import type { LifePlanState, EmploymentStatus, EducationLevel } from '@/types'
import { createInitialLifePlan } from '@/types/lifePlan.types'

// Helper to safely set nested object properties
function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown) {
  const keys = path.split('.')
  let current = obj
  
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    if (!(key in current) || typeof current[key] !== 'object') {
      current[key] = {}
    }
    current = current[key] as Record<string, unknown>
  }
  
  current[keys[keys.length - 1]] = value
}

// Helper to generate unique IDs
function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Transform questionnaire answers into a partial LifePlanState
 */
export function syncQuestionnaireToLifePlan(
  answers: Record<string, unknown>
): Partial<LifePlanState> {
  const lifePlan = createInitialLifePlan()
  
  // Map answers to state using question stateKeys
  allQuestions.forEach(question => {
    const answer = answers[question.id]
    if (answer === undefined || answer === null || answer === '') return
    
    // Handle special transformations
    const stateKey = question.stateKey
    
    // Skip complex array fields that need special handling
    if (!stateKey) return
    
    // Set the value at the correct path
    setNestedValue(lifePlan as unknown as Record<string, unknown>, stateKey, answer)
  })
  
  // Handle special cases that need transformation
  
  // Children ages -> children array
  const childrenAges = answers['family_children_ages'] as string | undefined
  const hasChildren = answers['family_has_children'] === 'yes'
  if (hasChildren && childrenAges) {
    const ages = childrenAges.split(',').map(a => parseInt(a.trim())).filter(a => !isNaN(a))
    const currentYear = new Date().getFullYear()
    const collegeFunding = answers['family_college_funding'] as string | undefined
    lifePlan.family.children = ages.map((age, index) => ({
      id: generateId('child'),
      firstName: `Child ${index + 1}`,
      birthDate: `${currentYear - age}-01-01`,
      collegeFundingPlan: (collegeFunding === 'full' ? 'full' : collegeFunding === 'partial' ? 'partial' : 'none') as 'none' | 'partial' | 'full',
      expectedCollegeCost: 30000,  // Default annual cost
      collegeYears: 4,
      isDependent: age < 18,
    }))
  }
  
  // Planned children
  const planningChildren = answers['family_planning_children']
  const plannedChildrenCount = answers['family_planned_children_count'] as number | undefined
  const firstChildYear = answers['family_first_child_year'] as number | undefined
  const collegeFundingPlan = answers['family_college_funding'] as string | undefined
  if ((planningChildren === 'yes' || planningChildren === 'maybe') && plannedChildrenCount) {
    lifePlan.family.plannedChildren = []
    for (let i = 0; i < plannedChildrenCount; i++) {
      lifePlan.family.plannedChildren.push({
        id: generateId('planned_child'),
        plannedYear: (firstChildYear || new Date().getFullYear() + 2) + i * 2,
        collegeFundingPlan: (collegeFundingPlan === 'full' ? 'full' : collegeFundingPlan === 'partial' ? 'partial' : 'none') as 'none' | 'partial' | 'full',
        expectedCollegeCost: 30000,
      })
    }
  }
  
  // Spouse info
  const maritalStatus = answers['family_marital_status'] as string | undefined
  if (maritalStatus === 'married' || maritalStatus === 'partnered') {
    lifePlan.family.spouse = {
      firstName: (answers['family_spouse_name'] as string) || '',
      birthDate: (answers['family_spouse_birth_date'] as string) || '',
      currentIncome: (answers['family_spouse_income'] as number) || 0,
      retirementAge: (answers['family_spouse_retirement_age'] as number) || 65,
      employmentStatus: 'employed',
    }
  }
  
  // Current job
  const employmentStatus = answers['career_employment_status'] as string | undefined
  if (employmentStatus === 'employed' || employmentStatus === 'self-employed') {
    lifePlan.career.currentJob = {
      title: (answers['career_job_title'] as string) || 'Current Position',
      employer: (answers['career_employer'] as string) || '',
      industry: (answers['career_industry'] as string) || 'Other',
      yearsInRole: 0,
      yearsExperience: (answers['career_years_experience'] as number) || 0,
      baseSalary: (answers['career_base_salary'] as number) || 0,
      bonus: (answers['career_bonus'] as number) || 0,
      commission: (answers['career_commission'] as number) || 0,
      stockCompensation: (answers['career_stock_comp'] as number) || 0,
      employerMatch401k: (answers['career_401k_match_percent'] as number) || 0,
      employerMatchLimit: 6, // Default 6% match limit
    }
  }
  lifePlan.career.employmentStatus = (employmentStatus || 'employed') as EmploymentStatus
  lifePlan.career.educationLevel = (answers['career_education'] as EducationLevel) || 'bachelors'
  
  // Side income
  const hasSideIncome = answers['career_side_income'] === 'yes'
  const sideIncomeAmount = answers['career_side_income_amount'] as number | undefined
  if (hasSideIncome && sideIncomeAmount) {
    lifePlan.career.sideIncomes = [{
      id: generateId('side_income'),
      name: 'Side Income',
      annualAmount: sideIncomeAmount,
      isRecurring: true,
    }]
  }
  
  // Create assets from finance questions
  lifePlan.assets = []
  
  const checking = answers['finances_checking'] as number | undefined
  if (checking) {
    lifePlan.assets.push({
      id: generateId('asset'),
      name: 'Checking Account',
      category: 'checking',
      currentValue: checking,
      expectedReturn: 0,
      contributions: 0,
      isRetirementAccount: false,
      taxAdvantaged: false,
    })
  }
  
  const savings = answers['finances_savings'] as number | undefined
  if (savings) {
    lifePlan.assets.push({
      id: generateId('asset'),
      name: 'Savings Account',
      category: 'savings',
      currentValue: savings,
      expectedReturn: 0.5,
      contributions: 0,
      isRetirementAccount: false,
      taxAdvantaged: false,
    })
  }
  
  const emergencyFund = answers['finances_emergency_amount'] as number | undefined
  if (emergencyFund) {
    lifePlan.assets.push({
      id: generateId('asset'),
      name: 'Emergency Fund',
      category: 'savings',
      currentValue: emergencyFund,
      expectedReturn: 0.5,
      contributions: 0,
      isRetirementAccount: false,
      taxAdvantaged: false,
    })
  }
  
  const investments = answers['finances_investment_value'] as number | undefined
  if (investments) {
    lifePlan.assets.push({
      id: generateId('asset'),
      name: 'Brokerage Account',
      category: 'brokerage',
      currentValue: investments,
      expectedReturn: 7,
      contributions: 0,
      isRetirementAccount: false,
      taxAdvantaged: false,
    })
  }
  
  const balance401k = answers['finances_401k_balance'] as number | undefined
  const contribution401k = answers['finances_401k_contribution'] as number | undefined
  const salary = answers['career_base_salary'] as number | undefined
  if (balance401k) {
    const annualContrib = contribution401k && salary ? (contribution401k / 100) * salary : 0
    lifePlan.assets.push({
      id: generateId('asset'),
      name: '401(k)',
      category: '401k',
      currentValue: balance401k,
      expectedReturn: 7,
      contributions: annualContrib,
      isRetirementAccount: true,
      taxAdvantaged: true,
    })
  }
  
  const iraBalance = answers['finances_ira_balance'] as number | undefined
  const iraType = answers['finances_has_ira'] as string | undefined
  if (iraBalance && iraType && iraType !== 'none') {
    lifePlan.assets.push({
      id: generateId('asset'),
      name: iraType === 'roth' ? 'Roth IRA' : iraType === 'both' ? 'IRAs' : 'Traditional IRA',
      category: iraType === 'roth' ? 'ira_roth' : 'ira_traditional',
      currentValue: iraBalance,
      expectedReturn: 7,
      contributions: 7000, // Max IRA contribution
      isRetirementAccount: true,
      taxAdvantaged: true,
    })
  }
  
  const hsaBalance = answers['finances_hsa_balance'] as number | undefined
  if (hsaBalance) {
    lifePlan.assets.push({
      id: generateId('asset'),
      name: 'HSA',
      category: 'hsa',
      currentValue: hsaBalance,
      expectedReturn: 5,
      contributions: 4150, // 2024 individual limit
      isRetirementAccount: false,
      taxAdvantaged: true,
    })
  }
  
  // Home equity
  const homeValue = answers['finances_home_value'] as number | undefined
  const mortgageBalance = answers['finances_mortgage_balance'] as number | undefined
  if (homeValue) {
    lifePlan.assets.push({
      id: generateId('asset'),
      name: 'Primary Residence',
      category: 'real_estate',
      currentValue: homeValue - (mortgageBalance || 0),
      expectedReturn: 3,
      contributions: 0,
      isRetirementAccount: false,
      taxAdvantaged: false,
    })
  }
  
  // Create debts
  lifePlan.debts = []
  
  if (mortgageBalance) {
    const mortgageRate = answers['finances_mortgage_rate'] as number | undefined
    lifePlan.debts.push({
      id: generateId('debt'),
      name: 'Mortgage',
      category: 'mortgage',
      balance: mortgageBalance,
      interestRate: mortgageRate || 6.5,
      minimumPayment: mortgageBalance * 0.006, // Rough estimate
      extraPayment: 0,
    })
  }
  
  const studentLoanBalance = answers['finances_student_loan_balance'] as number | undefined
  if (studentLoanBalance) {
    lifePlan.debts.push({
      id: generateId('debt'),
      name: 'Student Loans',
      category: 'student_federal',
      balance: studentLoanBalance,
      interestRate: 6,
      minimumPayment: studentLoanBalance * 0.01, // Rough IBR estimate
      extraPayment: 0,
    })
  }
  
  const creditCardBalance = answers['finances_credit_card_balance'] as number | undefined
  if (creditCardBalance) {
    lifePlan.debts.push({
      id: generateId('debt'),
      name: 'Credit Cards',
      category: 'credit_card',
      balance: creditCardBalance,
      interestRate: 22,
      minimumPayment: Math.max(25, creditCardBalance * 0.02),
      extraPayment: 0,
    })
  }
  
  const otherDebtBalance = answers['finances_other_debt_balance'] as number | undefined
  if (otherDebtBalance) {
    lifePlan.debts.push({
      id: generateId('debt'),
      name: 'Other Debt',
      category: 'other',
      balance: otherDebtBalance,
      interestRate: 8,
      minimumPayment: otherDebtBalance * 0.02,
      extraPayment: 0,
    })
  }
  
  // Insurance
  lifePlan.insurance.health.type = (answers['insurance_health_type'] as 'employer' | 'marketplace' | 'medicare' | 'medicaid' | 'none') || 'employer'
  lifePlan.insurance.health.monthlyPremium = (answers['insurance_health_premium'] as number) || 0
  lifePlan.insurance.health.deductible = (answers['insurance_health_deductible'] as number) || 0
  
  const hasLifeInsurance = answers['insurance_has_life'] === 'yes'
  if (hasLifeInsurance) {
    lifePlan.insurance.life = [{
      id: generateId('life_insurance'),
      type: (answers['insurance_life_type'] as 'term' | 'whole' | 'universal') || 'term',
      coverageAmount: (answers['insurance_life_coverage'] as number) || 0,
      monthlyPremium: (answers['insurance_life_premium'] as number) || 0,
      termLength: 20,
    }]
  }
  
  const hasDisability = answers['insurance_has_disability'] === 'yes'
  if (hasDisability) {
    lifePlan.insurance.disability.hasShortTerm = answers['insurance_disability_short_term'] === 'yes'
    lifePlan.insurance.disability.hasLongTerm = answers['insurance_disability_long_term'] === 'yes'
    lifePlan.insurance.disability.longTermCoverage = (answers['insurance_disability_coverage'] as number) || 60
  }
  
  lifePlan.insurance.autoAnnual = (answers['insurance_auto_annual'] as number) || 0
  lifePlan.insurance.homeAnnual = (answers['insurance_home_annual'] as number) || 0
  // hasUmbrella -> set umbrellaAnnual to a default if they have it
  if (answers['insurance_has_umbrella'] === 'yes') {
    lifePlan.insurance.umbrellaAnnual = 300 // Default umbrella policy cost
  }
  
  // Preferences
  lifePlan.preferences.riskTolerance = (answers['preferences_risk_tolerance'] as 'conservative' | 'moderate' | 'aggressive') || 'moderate'
  lifePlan.preferences.socialSecurityAge = (answers['preferences_social_security_age'] as number) || 67
  lifePlan.preferences.expectedSocialSecurity = (answers['preferences_social_security_estimate'] as number) || 2000
  lifePlan.preferences.hasPension = answers['preferences_has_pension'] === 'yes'
  lifePlan.preferences.pensionMonthly = (answers['preferences_pension_monthly'] as number) || 0
  lifePlan.preferences.pensionStartAge = (answers['preferences_pension_age'] as number) || 65
  // Set inheritance amount if expecting one
  const expectingInheritance = answers['preferences_expecting_inheritance']
  if (expectingInheritance === 'yes' || expectingInheritance === 'maybe') {
    lifePlan.preferences.expectedInheritance = (answers['preferences_inheritance_amount'] as number) || 0
    lifePlan.preferences.inheritanceYear = (answers['preferences_inheritance_year'] as number) || undefined
  }
  lifePlan.preferences.inflationRate = (answers['preferences_inflation_rate'] as number) || 2.5
  
  // Mark questionnaire as completed
  lifePlan.questionnaireCompleted = true
  lifePlan.questionnaireProgress = 100
  
  return lifePlan
}
